import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import FullPageSpinner from "../layout/FullPageSpinner";
import {
  fetchAllProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  bulkUpdateProducts
} from "../../actions/product";
import PopupDialog from "../layout/PopupDialog";
import ConfirmModal from "../layout/ConfirmModal";
import { toast } from "react-toastify";
// import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { classList } from "../../utils/constants";

const ProductManagement = () => {
   const { user } = useSelector(
    (state) => state.auth
  );

  const hasPermission = (permission) => {
    if (user?.role === 'superadmin') return true;
    return user?.permissions?.[permission] === true;
  };

  const canAddProduct = hasPermission('product_add');
  const canEditProduct = hasPermission('product_edit');
  const canDeleteProduct = hasPermission('product_delete');
  const canExportProducts = hasPermission('product_export');
  const canBulkUpdate = hasPermission('product_bulk_update');

  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    show: false,
    success: 0,
    failed: 0,
    errors: []
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await fetchAllProducts();
      setProducts(Array.isArray(response) ? response : []);
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const addProductClick = () => {
    const data = [
      {
        label: "Name",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Display Name",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Category Name",
        value: "",
        editable: true,
        options: null,
      },
       {
        label: "Category Order",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Price",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Bundle Price",
        value: "",
        editable: true,
        options: null,
      },
      {
        label: "Weight (in grams)",
        value: "",
        editable: true,
        options: null,
        type: "number",
        optional: true,
      },
      {
        label: "Image Url",
        value: "",
        editable: true,
        options: null,
        optional: true,
      },
      {
        label: "Size chart URL",
        value: "",
        editable: true,
        options: null,
        optional: true,
      },
      {
        label: "Available Sizes",
        value: [],
        editable: true,
        options: "Multiple Entry",
        optional: true,
      },
      {
        label: "Applicable Classes",
        value: [],
        editable: true,
        options: "Multi Select",
        selectOptions: classList.map(cls => ({ value: cls.key, label: cls.label })),
        optional: true,
      },
    ];
    setSelectedId(null);
    setSelectedRow(data);
    setShowPopup(true);
  };

  const onEditProductClick = (product) => {
    const data = [
      {
        label: "Name",
        value: product.name || "",
        editable: true,
        options: null,
      },
      {
        label: "Display Name",
        value: product.display_name || "",
        editable: true,
        options: null,
      },
      {
        label: "Category",
        value: product.category || "",
        editable: true,
        options: null,
      },
       {
        label: "Category Order",
        value: product.order || 1,
        editable: true,
        options: null,
      },
      {
        label: "Unit Price",
        value: product.price?.toString() || "",
        editable: true,
        options: null,
      },
       {
        label: "Bundle Price",
        value: product.bundle_price?.toString() || "",
        editable: true,
        options: null,
      },
      {
        label: "Weight (in grams)",
        value: (product.weight ?? product.weight_in_grams ?? "").toString(),
        editable: true,
        options: null,
        type: "number",
        optional: true,
      },
      {
        label: "Image URL",
        value: product.imageUrl || "",
        editable: true,
        options: null,
        optional: true,
      },
      {
        label: "Size chart URL",
        value: product.sizeChart || "",
        editable: true,
        options: null,
        optional: true,
      },
      {
        label: "Available Sizes",
        value: product.availableSizes || [],
        editable: true,
        options: "Multiple Entry",
        optional: true,
      },
      {
        label: "Applicable Classes",
        value: product.applicableClasses ? product.applicableClasses.split(", ").map(cls => {
          const classItem = classList.find(c => c.label === cls);
          return classItem ? { value: classItem.key, label: classItem.label } : null;
        }).filter(Boolean) : [],
        editable: true,
        options: "Multi Select",
        selectOptions: classList.map(cls => ({ value: cls.key, label: cls.label })),
        optional: true,
      },
    ];
    setSelectedId(product.id);
    setSelectedRow(data);
    setShowPopup(true);
  };

  const onDeleteClick = (id) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  const deleteConfirmClick = async () => {
    setLoading(true);
    try {
      await deleteProduct(selectedId);
      setProducts(products.filter(product => product.id !== selectedId));
    } catch (error) {
      toast.error("Failed to delete product", { position: "top-right" });
    } finally {
      setShowConfirm(false);
      setLoading(false);
      setSelectedId(null);
    }
  };

  const handleSave = async (formData) => {
    try {
      setLoading(true);
      const getFieldVal = (lbl, defaultVal = "") => {
        const item = formData.find((f) => f.label === lbl);
        return item !== undefined && item.value !== undefined ? item.value : defaultVal;
      };

      const productData = {
        name: getFieldVal("Name"),
        display_name: getFieldVal("Display Name"),
        category: getFieldVal("Category Name", getFieldVal("Category")),
        order: getFieldVal("Category Order", 1),
        price: parseFloat(getFieldVal("Price", getFieldVal("Unit Price", 0))) || 0,
        bundle_price: parseFloat(getFieldVal("Bundle Price", 0)) || 0,
        weight: parseFloat(getFieldVal("Weight (in grams)", 0)) || 0,
        imageUrl: getFieldVal("Image Url", getFieldVal("Image URL")),
        sizeChart: getFieldVal("Size chart URL"),
        availableSizes: getFieldVal("Available Sizes", []),
        applicableClasses: Array.isArray(getFieldVal("Applicable Classes"))
          ? getFieldVal("Applicable Classes").map((option) => option.label || option.value).join(", ")
          : (getFieldVal("Applicable Classes") || ""),
      };

      if (selectedId) {
        // Update existing product
        const response = await updateProduct(selectedId, productData);
        if (response && response.id) {
          setProducts(
            products.map((product) =>
              product.id === selectedId ? response : product
            )
          );
          setShowPopup(false);
          setSelectedId(null);
          setSelectedRow({});
        }
      } else {
        // Create new product
        const response = await createProduct(productData);
        if (response && response.id) {
          setProducts([response, ...products]);
          setShowPopup(false);
          setSelectedId(null);
          setSelectedRow({});
        }
      }
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Error saving product", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const categoryMatch = category
        ? product.category?.toLowerCase().includes(category.toLowerCase())
        : true;

      const nameMatch = searchTerm
        ? product.name?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const displayNameMatch = searchTerm
        ? product.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      return categoryMatch && (nameMatch || displayNameMatch);
    });
  }, [products, searchTerm, category]);

  const categories = useMemo(() => {
    return [...new Set(products.map(p => p.category).filter(Boolean))];
  }, [products]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );



  const handleViewSizeChart = (sizeChart) => {
    // const img = new Image();
    // img.onload = () => {
    //   window.open(sizeChart, '_blank');
    // };
    // img.onerror = () => {
    //   // If JPG fails, try PNG
    //   const pngImg = new Image();
    //   pngImg.onload = () => {
    //     window.open(sizeChart, '_blank');
    //   };
    //   pngImg.onerror = () => {
    //     toast.error("Size chart not found", { position: "top-right" });
    //   };
    //   pngImg.src = sizeChart;
    // };
    // img.src = sizeChart;
    window.open(sizeChart, '_blank');
  };

  const splitCsvRow = (row) => {
    return row.split(/,(?![^{]*\})/).map(v => v.trim());
  };

  const normalizeProduct = (p) => ({
    id: p.id,
    name: String(p.name),
    price: Number(p.unit_price),
    bundle_price: Number(p.bundle_price),
    weight: p.weight !== undefined && p.weight !== 'NULL' && p.weight !== '' ? Number(p.weight) : (p.weight_in_grams !== undefined && p.weight_in_grams !== 'NULL' && p.weight_in_grams !== '' ? Number(p.weight_in_grams) : 0),
    image: p.image === 'NULL' ? null : p.image,
    sizeChart: p.size_chart === 'NULL' ? null : p.size_chart,
    imageUrl: p.imageUrl === 'NULL' ? null : p.imageUrl,
    availableSizes: p.available_sizes,
    display_name: p.display_name === 'NULL' ? null : p.display_name,
    category: p.category === 'NULL' ? null : p.category,
    order: Number(p.order),
    applicableClasses: p.applicable_classes === 'NULL' ? null : p.applicable_classes,
  });
  // Handle file upload for bulk updates
  const fileInputRef = useRef(null);
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n').filter(line => line.trim() !== '');
        const headers = lines[0].split(',').map(h => h.trim());

        const requiredColumns = ['name'];

        const updatableColumns = new Set([
          'name',
          'id',
          'unit_price',
          'bundle_price',
          'weight',
          'weight_in_grams',
          'image',
          'size_chart',
          'available_sizes',
          'display_name',
          'category',
          'applicable_classes',
        ]);


        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        if (missingColumns.length > 0) {
          toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
          setLoading(false);
          return;
        }

        const result = [];
        const invalidRows = [];

        for (let i = 1; i < lines.length; i++) {
          const values = splitCsvRow(lines[i]);

          if (values.length !== headers.length) {
            invalidRows.push(`Row ${i + 1}: column count mismatch`);
            continue;
          }

          const obj: any = {};

          headers.forEach((header, index) => {
            if (!updatableColumns.has(header) && header !== 'id') return;

            let value = values[index];

            // Parse available_sizes like {2,3,4,5}
            if (header === 'available_sizes') {
              value = value
                ? value
                    .replace(/[{}"]/g, '')
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean)
                : [];
            }
            if (value === '' || value === null || value === undefined) return;

            obj[header] = value;
          });

          // if (!obj.id) {
          //   invalidRows.push(`Row ${i + 1}: Missing product id`);
          //   continue;
          // }

          result.push(normalizeProduct(obj));
        }

        if (invalidRows.length > 0) {
          setUploadStatus({
            show: true,
            success: result.length,
            failed: invalidRows.length,
            errors: invalidRows.slice(0, 5)
          });
          setLoading(false);
          return;
        }

        if (result.length === 0) {
          toast.error("No valid data found in the file");
          setLoading(false);
          return;
        }

        const response =
          await bulkUpdateProducts(result);
        setUploadStatus({
          show: true,
          success: response.successCount || 0,
          failed: response.failedCount || 0,
          errors: [response.message]
        });
        loadProducts();
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.error("Failed to parse CSV file");
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    };

    reader.readAsText(file);
  };
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const exportProducts = async () => {
    setLoading(true);
    try {
      const headers = [
        'id',
        'name',
        'unit_price',
        'bundle_price',
        'weight',
        'image',
        'size_chart',
        'available_sizes',
        'display_name',
        'category',
        'applicable_classes'
      ];

      const rows = [];
      const productsExport = products || [];

      productsExport.forEach(product => {
        rows.push([
          product.id ?? '',
          product.name ?? '',
          product.price ?? '',
          product.bundle_price ?? '',
          product.weight ?? product.weight_in_grams ?? '',
          product.imageUrl ?? '',
          product.sizeChart ?? '',
          Array.isArray(product.availableSizes)
            ? `{${product.availableSizes.join(',')}}`
            : '',
          product.display_name ?? '',
          product.category ?? '',
          product.applicableClasses ?? ''
        ]);
      });

      // Build CSV
      let csvContent = headers.join(',') + '\n';

      rows.forEach(row => {
        const escapedRow = row.map(field => {
          const str = String(field ?? '');
          return str.includes(',') ? `"${str}"` : str;
        });
        csvContent += escapedRow.join(',') + '\n';
      });

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Products_Export.csv';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      toast.success('Products exported successfully');
    } catch (error) {
      console.error('Error exporting products:', error);
      toast.error('Failed to export products');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .cursor-pointer { cursor: pointer; }
        .table-responsive::-webkit-scrollbar { height: 8px; }
        .table-responsive::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 4px; }
        .filter-card { transition: all 0.3s ease; }
        .action-btn { transition: transform 0.2s; white-space: nowrap; }
        .action-btn:hover { transform: translateY(-1px); }
        .table th { white-space: nowrap; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; }
        .table td { font-size: 0.9rem; }
        .pagination-container { border-top: 1px solid #dee2e6; padding-top: 1.5rem; }
      `}</style>
      {loading && <FullPageSpinner loading={loading} />}
      <div className="bg-light min-vh-100 py-4">
        <div className="container-fluid px-lg-4">
          <div className="card shadow-sm border-0 rounded-3">
            <div className="card-header bg-white border-bottom py-3 px-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="mb-0 text-primary fw-bold d-flex align-items-center">
                  <i className="bi bi-box-seam-fill me-2"></i>Product Management
                </h4>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-pill px-3 py-2">Total: {filteredProducts.length}</span>
                </div>
              </div>
            </div>
            
            <div className="card-body p-4">
              {/* Filters & Actions Section */}
              <div className="bg-white rounded-3 p-4 mb-4 border shadow-sm filter-card">
                <div className="row g-3 align-items-end mb-4">
                  <div className="col-12 col-md-6 col-lg-4">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Search Products
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="form-label small fw-bold text-muted text-uppercase mb-1">
                      Category
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-lg-5">
                    <div className="d-flex flex-wrap justify-content-lg-end gap-2">
                      <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                      <Link
                        to="/admin/size-weights"
                        className="btn btn-outline-dark btn-sm action-btn px-3 fw-bold shadow-sm"
                        title="Configure size-wise weights"
                      >
                        <i className="bi bi-speedometer2 me-1"></i> Size Weights
                      </Link>
                      {canBulkUpdate && (
                        <button
                          className="btn btn-outline-primary btn-sm action-btn px-3 fw-bold shadow-sm"
                          onClick={triggerFileInput}
                        >
                          <i className="bi bi-upload me-1"></i> Bulk Update
                        </button>
                      )}
                      {canExportProducts && (
                        <button
                          className="btn btn-outline-info btn-sm action-btn px-3 fw-bold shadow-sm"
                          onClick={exportProducts}
                        >
                          <i className="bi bi-download me-1"></i> Export
                        </button>
                      )}
                      {canAddProduct && (
                        <button
                          className="btn btn-success btn-sm action-btn px-3 fw-bold shadow-sm"
                          onClick={addProductClick}
                        >
                          <i className="bi bi-plus-circle-fill me-1"></i> Add Product
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {uploadStatus.show && (
                <div className={`alert ${uploadStatus.failed > 0 ? 'alert-warning' : 'alert-success'} alert-dismissible fade show rounded-3 shadow-sm mb-4`} role="alert">
                  <div className="d-flex align-items-center mb-2">
                    <i className={`bi ${uploadStatus.failed > 0 ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2 fs-5`}></i>
                    <h5 className="mb-0">Bulk Update Results</h5>
                  </div>
                  <div className="row g-2">
                    <div className="col-auto">
                      <span className="badge bg-success">Success: {uploadStatus.success}</span>
                    </div>
                    {uploadStatus.failed > 0 && (
                      <div className="col-auto">
                        <span className="badge bg-danger">Failed: {uploadStatus.failed}</span>
                      </div>
                    )}
                  </div>
                  {uploadStatus.failed > 0 && (
                    <div className="mt-3 p-2 bg-white rounded border" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      <p className="small fw-bold text-muted mb-1">Error Logs:</p>
                      <ul className="list-unstyled small mb-0">
                        {uploadStatus.errors.map((error, index) => (
                          <li key={index} className="text-danger mb-1">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button type="button" className="btn-close" onClick={() => setUploadStatus({ ...uploadStatus, show: false })} aria-label="Close"></button>
                </div>
              )}

              {/* Table Section */}
              <div className="table-responsive rounded-3 border shadow-sm bg-white">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '80px' }}>ID</th>
                      <th>Name</th>
                      <th>Display Name</th>
                      <th>Suitable Classes</th>
                      <th>Category</th>
                      <th>Image</th>
                      <th>Size Chart</th>
                      <th className="text-end">Unit Price</th>
                      <th className="text-end">Bundle Price</th>
                      <th className="text-end">Weight (g)</th>
                      <th>Created At</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.length > 0 ? (
                      paginatedProducts.map((product) => (
                        <tr key={product.id}>
                          <td className="text-muted small">#{product.id}</td>
                          <td>
                            <div className="fw-semibold">{product.name}</div>
                          </td>
                          <td>{product.display_name}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '150px' }} title={product.applicableClasses}>
                              {product.applicableClasses}
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">{product.category}</span>
                          </td>
                          <td>
                            {product.imageUrl ? (
                              <button 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleViewSizeChart(product.imageUrl);
                                }} 
                                className="btn btn-link btn-sm p-0 text-decoration-none"
                              >
                                <i className="bi bi-image me-1"></i> View
                              </button>
                            ) : (
                              <span className="text-muted small">N/A</span>
                            )}
                          </td>
                          <td>
                            {product.sizeChart ? (
                              <button 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleViewSizeChart(product.sizeChart);
                                }} 
                                className="btn btn-link btn-sm p-0 text-decoration-none"
                              >
                                <i className="bi bi-image me-1"></i> View
                              </button>
                            ) : (
                              <span className="text-muted small">N/A</span>
                            )}
                          </td>
                          <td className="text-end fw-bold">₹{product.price}</td>
                          <td className="text-end fw-bold text-primary">₹{product.bundle_price}</td>
                          <td className="text-end fw-semibold">
                            {product.weight ? `${product.weight} g` : product.weight_in_grams ? `${product.weight_in_grams} g` : "-"}
                          </td>
                          <td className="small text-muted">{new Date(product.createdAt).toLocaleDateString()}</td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              {canEditProduct && (
                                <button
                                  className="btn btn-outline-primary btn-sm rounded-circle"
                                  onClick={() => onEditProductClick(product)}
                                  title="Edit Product"
                                  style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              {canDeleteProduct && (
                                <button
                                  className="btn btn-outline-danger btn-sm rounded-circle"
                                  onClick={() => onDeleteClick(product.id)}
                                  title="Delete Product"
                                  style={{ width: '32px', height: '32px', padding: 0 }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center py-5 text-muted">
                          <i className="bi bi-box-fill fs-1 d-block mb-2"></i>
                          No products found matching your criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Section */}
              <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                <div className="d-flex align-items-center gap-3 order-2 order-md-1">
                  <div className="d-flex align-items-center bg-white border rounded px-2 py-1 shadow-sm">
                    <span className="text-muted small me-2">Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="form-select form-select-sm border-0 bg-transparent fw-bold"
                      style={{ width: 'auto', outline: 'none', boxShadow: 'none' }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-muted small ms-2">entries</span>
                  </div>
                  <div className="text-muted small border-start ps-3 d-none d-sm-block">
                    Showing <span className="fw-bold text-dark">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="fw-bold text-dark">{Math.min(currentPage * itemsPerPage, filteredProducts.length)}</span> of <span className="fw-bold text-dark">{filteredProducts.length}</span> records
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 order-1 order-md-2">
                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    <i className="bi bi-chevron-left"></i> Prev
                  </button>
                  
                  <div className="bg-white px-3 py-1 rounded-pill border shadow-sm small fw-bold">
                    Page {currentPage} of {totalPages}
                  </div>

                  <button
                    className="btn btn-outline-primary btn-sm px-3 rounded-pill shadow-sm fw-bold"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPopup && (
        <PopupDialog
          data={selectedRow}
          onSave={handleSave}
          onCancel={() => setShowPopup(false)}
          header={selectedId ? "Edit Product" : "Add Product"}
        />
      )}
      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={deleteConfirmClick}
        message="Are you sure you want to delete this product?"
      />
    </>
  );
};

export default ProductManagement;
